import type { KeyboardEvent } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FUNNEL_STAGE_LABELS,
  HelpTip,
  formatDate,
  PersonDisplay,
} from '@dataroom/shared';
import type { BuyerAccessItem } from '@dataroom/shared';

interface CimAccessCardProps {
  buyers: BuyerAccessItem[];
  loading: boolean;
  error: string | null;
  /** Open the per-buyer detail view (advance/roll back stage, edit expiry, revoke).
   * When omitted the rows are read-only. */
  onSelectBuyer?: (buyer: BuyerAccessItem) => void;
}

/** The buyer's current access level in one plain phrase, from the funnel gates. */
function accessLevel(b: BuyerAccessItem): string {
  // Reaching due diligence opens what the admin grants — not every file (ADR-0010 §2).
  if (b.allFilesUnlocked) return 'Due diligence — as granted';
  if (b.cimUnlocked) return 'CIM unlocked';
  if (b.ndaSigned) return 'NDA signed';
  return 'Teaser only';
}

function fmtExpiry(iso: string | null): string {
  return iso ? formatDate(iso) : 'Ongoing';
}

/**
 * PRD-0004 §3e CIM management — the CIM parallel to §3c NDA management: who can
 * currently see the CIM. Each buyer in the funnel with what their stage has
 * unlocked (NDA sealed → CIM → all files). Read view: access is funnel-gated
 * (sealing the NDA unlocks the CIM), so this shows the gate rather than editing
 * it; the admin moves a buyer via the LOI/funnel controls.
 */
export function CimAccessCard({ buyers, loading, error, onSelectBuyer }: CimAccessCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          CIM &amp; document access <HelpTip id="admin.buyers.cim-access" className="ml-1" />
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Who can currently see the CIM. Access is funnel-gated — sealing the NDA unlocks the CIM,
          and from the LOI on, due diligence opens the folders you grant each buyer.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : buyers.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 font-medium">Buyer</th>
                <th className="py-2 font-medium">Current access</th>
                <th className="py-2 font-medium">Stage</th>
                <th className="py-2 font-medium">Expires</th>
              </tr>
            </thead>
            <tbody>
              {buyers.map((b) => (
                <tr
                  key={b.actorId}
                  className={`border-b last:border-0 ${
                    onSelectBuyer
                      ? 'cursor-pointer hover:bg-muted/50 focus-within:bg-muted/50'
                      : ''
                  }`}
                  {...(onSelectBuyer
                    ? {
                        role: 'button',
                        tabIndex: 0,
                        'aria-label': `Manage access for ${b.actorName?.trim() || b.actorEmail || b.actorId}`,
                        onClick: () => onSelectBuyer(b),
                        onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelectBuyer(b);
                          }
                        },
                      }
                    : {})}
                >
                  <td className="py-2 pr-4 font-medium">
                    <PersonDisplay
                      name={b.actorName}
                      email={b.actorEmail}
                      fallback={b.actorId}
                      size="sm"
                    />
                  </td>
                  <td className="py-2 pr-4">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        b.cimUnlocked
                          ? 'bg-st-certified text-st-certified-fg'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {accessLevel(b)}
                    </span>
                  </td>
                  <td className="py-2 pr-4">{FUNNEL_STAGE_LABELS[b.stage]}</td>
                  <td className="py-2 pr-4">{fmtExpiry(b.accessExpiresAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-muted-foreground">
            No buyers in the funnel yet. Invite a buyer and issue their NDA to begin.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
